/**
 * State Immersion Experience
 * The core daily practice - transforms identity through state induction
 * 
 * Flow: State Induction → Immersion → Tool → Bathe
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { smartBack } from '../../utils/smartBack';
import RLIImmersionPlayer from '../../components/LivingDream/RLIImmersionPlayer';
import { useImmersiveMode } from '../../hooks/useImmersiveMode';
import { 
  Sparkles, 
  Heart, 
  Sun, 
  Home, 
  Compass, 
  Zap,
  ArrowRight,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// State configurations
const STATE_CONFIG = {
  wealth: {
    title: 'Wealth',
    phrase: 'It is safe for me to receive more',
    inductionText: 'Feel the ease of abundance',
    icon: Sparkles,
    color: '#CBA153',
    toolPath: '/bank-of-reclaim',
    toolName: 'Bank of Reclaim',
    altToolPath: '/abundance/winning-moment',
    altToolName: 'Winning Moment',
    immersionAffirmations: [
      'Money flows to me naturally',
      'I am a magnet for prosperity',
      'Wealth is my birthright',
      'I receive with grace',
      'Abundance is all around me',
    ],
  },
  love: {
    title: 'Love',
    phrase: 'I am worthy of deep connection',
    inductionText: 'Open to receiving love',
    icon: Heart,
    color: '#E8A4A4',
    toolPath: null, // Internal tool
    toolName: 'Connection Experience',
    immersionAffirmations: [
      'I am deeply loved',
      'Connection is natural for me',
      'My heart is open and safe',
      'Love flows to me effortlessly',
      'I deserve beautiful relationships',
    ],
  },
  body: {
    title: 'Body',
    phrase: 'My body is my trusted companion',
    inductionText: 'Feel your vitality',
    icon: Sun,
    color: '#7BA378',
    toolPath: null, // Internal tool
    toolName: 'Embodiment Experience',
    immersionAffirmations: [
      'My body is wise and strong',
      'Vitality flows through me',
      'I honor this vessel',
      'Energy is abundant within me',
      'I am healthy and whole',
    ],
  },
  performance: {
    title: 'Performance',
    phrase: 'I am composed, focused, ready',
    inductionText: 'Activate your peak state',
    icon: Zap,
    color: '#F59E0B',
    toolPath: null, // Internal Peak State tool
    toolName: 'Peak State Activation',
    toolDeepPath: '/private-suite', // Option to go deeper
    immersionAffirmations: [
      'I am ready for this moment',
      'Focus comes naturally to me',
      'I perform at my highest level',
      'Calm confidence flows through me',
      'I execute with precision',
    ],
  },
  home: {
    title: 'Home',
    phrase: 'I belong in beautiful spaces',
    inductionText: 'Feel the safety of belonging',
    icon: Home,
    color: '#A89080',
    toolPath: null, // Internal tool
    toolName: 'Future Space',
    immersionAffirmations: [
      'I belong in beautiful spaces',
      'My home holds me',
      'I create sanctuary wherever I am',
      'Beauty surrounds me naturally',
      'I deserve a beautiful life',
    ],
  },
  freedom: {
    title: 'Freedom',
    phrase: 'I design my own path',
    inductionText: 'Expand into possibility',
    icon: Compass,
    color: '#7BA3B8',
    toolPath: null, // Internal tool
    toolName: 'Expansion Experience',
    immersionAffirmations: [
      'I am free to choose',
      'My path is mine to design',
      'Possibility surrounds me',
      'I live life on my terms',
      'Freedom is my natural state',
    ],
  },
};

// Phase 3: Tool Transition
const ToolTransition = ({ config, onNavigate, onAltTool, onBathe }) => {
  const Icon = config.icon;
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: '#121210' }}
    >
      <div className="text-center px-8 max-w-md">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="w-20 h-20 rounded-full mx-auto mb-8 flex items-center justify-center"
          style={{ backgroundColor: `${config.color}20` }}
        >
          <Icon size={32} style={{ color: config.color }} strokeWidth={1.5} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-4"
        >
          {config.title}
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="font-['Playfair_Display'] text-2xl text-[#F2EFE9] mb-12"
        >
          "{config.phrase}"
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-4"
        >
          {/* Main Tool Button */}
          <button
            onClick={onNavigate}
            className="w-full py-5 rounded-2xl bg-[#1A1A18] border border-[#2C2C28] hover:border-[#4A6741]/40 transition-all duration-500 flex items-center justify-center gap-3"
            data-testid="continue-to-tool"
          >
            <span className="text-[#F2EFE9] text-lg font-light">
              Continue to {config.toolName}
            </span>
            <ArrowRight size={18} className="text-[#9E9A90]" />
          </button>

          {/* Alternative Tool Option (for Wealth - Winning Moment) */}
          {config.altToolPath && (
            <button
              onClick={() => navigate(config.altToolPath)}
              className="w-full py-4 rounded-2xl border border-[#CBA153]/30 hover:border-[#CBA153]/60 text-[#CBA153] text-sm transition-all duration-500"
            >
              Or try {config.altToolName}
            </button>
          )}

          {/* Go Deeper Option (for Performance) */}
          {config.toolDeepPath && (
            <button
              onClick={() => navigate(config.toolDeepPath)}
              className="text-[#9E9A90] text-sm hover:text-[#F2EFE9] transition-colors duration-300"
            >
              Go deeper with Private Suite
            </button>
          )}

          {/* Bathe Option */}
          <button
            onClick={onBathe}
            className="text-[#9E9A90] text-sm hover:text-[#F2EFE9] transition-colors duration-300"
          >
            Or immerse in Bathe experience
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Peak State Activation Tool (Performance-specific)
const PeakStateActivation = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const phases = [
    { title: 'Reset', duration: 10, instruction: 'Release tension... breathe...' },
    { title: 'Identity', duration: 15, instruction: 'You are composed, focused, ready' },
    { title: 'Visualize', duration: 20, instruction: 'See yourself executing perfectly' },
    { title: 'Activate', duration: 15, instruction: 'Feel your energy rising' },
  ];

  useEffect(() => {
    if (phase >= phases.length) {
      setTimeout(onComplete, 1000);
      return;
    }

    const timer = setTimeout(() => {
      setPhase(prev => prev + 1);
    }, phases[phase].duration * 1000);

    return () => clearTimeout(timer);
  }, [phase, phases, onComplete]);

  if (phase >= phases.length) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 flex items-center justify-center bg-[#121210]"
      >
        <motion.h2
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="font-['Playfair_Display'] text-4xl text-[#F2EFE9]"
        >
          You are ready
        </motion.h2>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 flex items-center justify-center bg-[#121210]"
    >
      <div className="text-center px-8">
        <motion.p
          key={`phase-${phase}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#F59E0B] text-xs uppercase tracking-[0.25em] mb-4"
        >
          {phases[phase].title}
        </motion.p>
        <motion.h2
          key={`instruction-${phase}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-['Playfair_Display'] text-3xl text-[#F2EFE9]"
        >
          {phases[phase].instruction}
        </motion.h2>

        {/* Progress */}
        <div className="flex justify-center gap-2 mt-12">
          {phases.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: i <= phase ? '#F59E0B' : '#2C2C28' }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Master Immersion layers per state: the vista journey and the images pulsing around the dot.
const STATE_VISTAS = {
  "wealth": {
    "video": "/api/atelier/journey-video/vista/gardens",
    "images": [
      "https://images.unsplash.com/photo-1780357543302-e6f02225ea29?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1650394850853-bad61e0f6c75?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1630494550902-23a1e6027fc5?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1694552514751-b870caaada29?w=1600&fit=crop"
    ]
  },
  "love": {
    "video": "/api/atelier/journey-video/vista/lakes",
    "images": [
      "https://images.unsplash.com/photo-1591969851586-adbbd4accf81?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1521033719794-41049d18b8d4?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?w=1600&fit=crop",
      "https://images.pexels.com/photos/14391560/pexels-photo-14391560.jpeg?auto=compress&cs=tinysrgb&w=1600"
    ]
  },
  "body": {
    "video": "/api/atelier/journey-video/vista/gardens",
    "images": [
      "https://images.unsplash.com/photo-1636619297905-54f124aa90b2?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1646166624936-d93c08117e02?w=1600&fit=crop",
      "https://images.pexels.com/photos/7529035/pexels-photo-7529035.jpeg?auto=compress&cs=tinysrgb&w=1600",
      "https://images.unsplash.com/photo-1591228127791-8e2eaef098d3?w=1600&fit=crop"
    ]
  },
  "performance": {
    "video": "/api/atelier/journey-video/vista/mountains",
    "images": [
      "https://images.unsplash.com/photo-1576992018290-d3651509d49d?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1497561813398-8fcc7a37b567?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1501743411739-de52ea0ce6a0?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1499540633125-484965b60031?w=1600&fit=crop"
    ]
  },
  "home": {
    "video": "/api/atelier/journey-video/vista/lakes",
    "images": [
      "https://images.unsplash.com/photo-1649083048337-4aeb6dda80bb?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1649083048597-d7b4f1e8a386?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1554995207-c18c203602cb?w=1600&fit=crop"
    ]
  },
  "freedom": {
    "video": "/api/atelier/journey-video/vista/mountains",
    "images": [
      "https://images.unsplash.com/photo-1494783367193-149034c05e8f?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1585552622005-f6c9228f6b68?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1523251343397-9225e4cb6319?w=1600&fit=crop",
      "https://images.unsplash.com/photo-1478031706604-bb4b7b0b4e9e?w=1600&fit=crop"
    ]
  }
};

// Main Component
export const StateImmersion = () => {
  const { stateId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [phase, setPhase] = useState('immersion'); // immersion (Master Immersion player), tool, peakState
  useImmersiveMode(phase === 'immersion');
  const [hasRecordedEntry, setHasRecordedEntry] = useState(false);

  const config = STATE_CONFIG[stateId];

  // Record entry for streak
  const recordEntry = useCallback(async () => {
    if (hasRecordedEntry) return;
    
    const token = localStorage.getItem('reclaim_token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/abundance/record-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ state: stateId })
      });
      setHasRecordedEntry(true);
    } catch (error) {
      console.error('Failed to record entry:', error);
    }
  }, [stateId, hasRecordedEntry]);

  useEffect(() => {
    // Record entry when immersion starts
    if (phase === 'immersion') {
      recordEntry();
    }
  }, [phase, recordEntry]);

  if (!config) {
    navigate('/abundance');
    return null;
  }

  const handleToolNavigation = () => {
    if (stateId === 'performance') {
      // Navigate to the full Peak State Engine
      navigate('/abundance/peak-state');
    } else if (config.toolPath) {
      // External tool (like Bank of Reclaim)
      navigate(config.toolPath);
    } else {
      // For states without external tools, go to Bathe
      navigate(`/abundance/${stateId}/bathe`);
    }
  };

  const handleBathe = () => {
    navigate(`/abundance/${stateId}/bathe`);
  };

  const handlePeakStateComplete = () => {
    navigate(`/abundance/${stateId}/bathe`);
  };

  return (
    <div className="min-h-screen bg-[#121210]">
      {/* Back/close — single, prominent, always escapeable. Uses
          navigate(-1) so it returns to whatever route launched the
          immersion (Daily Essentials, Saved Visions, deep-link). */}
      {phase !== 'immersion' && <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => smartBack(navigate, pathname)}
        className="fixed top-4 left-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-md transition-all"
        style={{
          background: 'rgba(20, 28, 44, 0.65)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 4px 18px -8px rgba(0,0,0,0.5)',
        }}
        data-testid="close-immersion"
        aria-label="Back"
      >
        <X size={16} className="text-white/85" />
        <span className="text-[11px] tracking-[0.18em] uppercase text-white/85 font-medium pr-1">
          Exit
        </span>
      </motion.button>}

      <AnimatePresence mode="wait">
        {phase === 'immersion' && (
          <RLIImmersionPlayer
            key="immersion"
            coachArea="abundance"
            journey={{ title: `${config.title} Immersion`, video_url: STATE_VISTAS[stateId].video }}
            dream={{
              vision_board: STATE_VISTAS[stateId].images.map((url) => ({ url })),
              affirmations: [config.phrase, ...config.immersionAffirmations].map((text) => ({ text })),
            }}
            onClose={() => setPhase('tool')}
          />
        )}
        {phase === 'tool' && (
          <ToolTransition
            key="tool"
            config={config}
            onNavigate={handleToolNavigation}
            onBathe={handleBathe}
          />
        )}
        {phase === 'peakState' && (
          <PeakStateActivation
            key="peakState"
            onComplete={handlePeakStateComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StateImmersion;
